import { useState } from 'react';
import { ChevronDown, ChevronUp, Waypoints } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * A quiet, collapsible "connections" legend pinned to a corner of the map. It
 * lists the top cross-district connections as e.g.
 *
 *   Drainage ↔ Development — detention, grading
 *
 * so a viewer grasps the municipality's knowledge structure in seconds without
 * touching the map. Light, unobtrusive, planning-styled. Hovering a row asks the
 * parent to highlight that district pair on the map.
 */

export interface LegendConnection {
  key: string;
  sourceLabel: string;
  targetLabel: string;
  sharedTerms: string[];
  /**
   * A written sentence describing the connection (backend prose pass). When
   * present, the row reveals it on hover (clearer than the term list); otherwise
   * it falls back to showing the shared terms.
   */
  rationale?: string;
  sourceDistrictId: number;
  targetDistrictId: number;
}

interface ConnectionsLegendProps {
  connections: LegendConnection[];
  /** Hover a row -> highlight that cross-district link on the map (null = clear). */
  onHoverPair: (pair: { sourceDistrictId: number; targetDistrictId: number } | null) => void;
}

// --- Tunable legend tokens ---
const LEGEND = {
  maxRows: 6, // only the most significant cross-district links
  termsPerRow: 3, // cap themes shown per row so it stays a strip, not a wall
} as const;

export function ConnectionsLegend({ connections, onHoverPair }: ConnectionsLegendProps) {
  const [open, setOpen] = useState(true);
  // Which row is hovered, so we can reveal its written rationale sentence.
  const [hoveredKey, setHoveredKey] = useState<string | null>(null);
  if (connections.length === 0) return null;

  const rows = connections.slice(0, LEGEND.maxRows);

  return (
    <div
      className="absolute bottom-4 left-4 z-20 w-[300px] overflow-hidden rounded-lg border border-slate-200 bg-white/92 backdrop-blur"
      style={{ boxShadow: '0 10px 30px -18px rgba(15,23,42,0.28)' }}
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center gap-2 px-3 py-2 text-left transition-colors hover:bg-slate-50"
      >
        <Waypoints className="h-3.5 w-3.5 shrink-0 text-slate-400" />
        <span className="text-[10px] font-medium uppercase tracking-[0.16em] text-slate-500">
          Cross-District Connections
        </span>
        <span className="ml-auto text-slate-400">
          {open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronUp className="h-3.5 w-3.5" />}
        </span>
      </button>

      {open && (
        <ul
          className="max-h-[40vh] overflow-y-auto border-t border-slate-100 px-1.5 py-1.5"
          onMouseLeave={() => {
            onHoverPair(null);
            setHoveredKey(null);
          }}
        >
          {rows.map((c) => {
            // On hover, reveal the written rationale sentence if we have one;
            // otherwise keep showing the (capped) shared-term list.
            const hovered = hoveredKey === c.key;
            const showRationale = hovered && !!c.rationale;
            return (
              <li key={c.key}>
                <div
                  className={cn(
                    'rounded-md px-2 py-1.5 transition-colors hover:bg-slate-100'
                  )}
                  onMouseEnter={() => {
                    setHoveredKey(c.key);
                    onHoverPair({
                      sourceDistrictId: c.sourceDistrictId,
                      targetDistrictId: c.targetDistrictId,
                    });
                  }}
                >
                  <div className="flex items-center gap-1.5 text-[12px] font-medium text-slate-700">
                    <span className="truncate">{c.sourceLabel}</span>
                    <span className="shrink-0 text-slate-300">↔</span>
                    <span className="truncate">{c.targetLabel}</span>
                  </div>
                  {showRationale ? (
                    <div className="mt-0.5 text-[11px] leading-snug text-slate-500">
                      {c.rationale}
                    </div>
                  ) : (
                    c.sharedTerms.length > 0 && (
                      <div className="mt-0.5 truncate text-[11px] text-slate-500">
                        {c.sharedTerms.slice(0, LEGEND.termsPerRow).join(', ')}
                      </div>
                    )
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
